import { DeckLayer } from '@deck.gl/arcgis';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { useEffect, useState } from 'react';
import { ESRI, GOOGLE } from '@rsos/maps/constants/mapProviders';
import exportToGlobalNamespace from '@rsos/utils/exportToGlobalNamespace';
import usePrevious from '@rsos/utils/usePrevious';
import DeckOverlay from './DeckOverlay';

/**
 * Custom hook to manage DeckGL overlays for different map providers (Esri, Google Maps).
 * @param {String} mapProvider - The map provider type, e.g., Esri or Google Maps.
 * @param {String} googleMapId - Optional Google Map ID used to configure DeckGL overlay.
 */
const useDeckOverlay = (mapProvider = '', googleMapId = '') => {
  // Holds reference to the provider's overlay.
  const [overlay, setOverlay] = useState(null);

  // Holds the unified overlay adapter.
  const [deckOverlay, setDeckOverlay] = useState(null);

  // Tracks the previous value of the Google Map ID to detect changes.
  const prevGoogleMapId = usePrevious(googleMapId);

  /**
   * Resets the DeckGL overlay when the Google Map ID changes and is different from the previous ID.
   * Ensures that the overlay is recreated to reflect the new map configuration.
   */
  useEffect(() => {
    if (mapProvider === GOOGLE && overlay && googleMapId !== prevGoogleMapId) {
      setDeckOverlay(null);
    }
  }, [googleMapId, mapProvider, overlay, prevGoogleMapId]);

  /**
   * Creates and sets a new DeckGL overlay adapter whenever the `overlay` instance changes.
   * The adapter acts as a bridge between DeckGL and the specific map provider.
   */
  useEffect(() => {
    if (overlay) {
      const deckOverlay = new DeckOverlay(mapProvider, overlay);
      setDeckOverlay(deckOverlay);
    }
  }, [overlay, mapProvider]);

  const createOverlay = (handleWebGLInitialized = () => {}) => {
    switch (mapProvider) {
      case ESRI: {
        return new DeckLayer();
      }
      case GOOGLE: {
        const overlay = new GoogleMapsOverlay({
          useDevicePixels: 2,
          onWebGLInitialized: gl => {
            handleWebGLInitialized(overlay, gl);
          },
        });
        return overlay;
      }
      default: {
        return null;
      }
    }
  };

  const handleOverlayCreation = async () => {
    const overlay = await createOverlay(handleWebGLInitialized);
    setOverlay(overlay);
    exportToGlobalNamespace('deckgl', overlay);
  };

  // TODO: This might only work for GMaps. Check
  // if this works for ESRI as well. For example,
  // what type is o?
  function handleWebGLInitialized(o, gl) {
    gl?.canvas?.addEventListener(
      'webglcontextlost',
      () => {
        o.setMap(null);
        o.finalize();
        handleOverlayCreation();
      },
      false,
    );
  }

  /**
   * Initializes the overlay whenever the Google Map ID changes.
   * Ensures the overlay is recreated with the updated configuration.
   */
  useEffect(() => {
    handleOverlayCreation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleMapId]);

  return deckOverlay;
};

export default useDeckOverlay;
